import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

export const ProFAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqItems: FAQItem[] = [
    {
      question: "Les résultats sont-ils fiables ?",
      answer: "DPEWizard récupère les données officielles ADEME et les enrichit avec un questionnaire contextuel pour affiner les recommandations et les estimations de coût. Les simulations restent des estimations, comme tout DPE projeté, y compris ceux réalisés avec le logiciel officiel de l'ADEME. Aucun DPE projeté n'est opposable. Le seul document opposable est le DPE formel réalisé par un diagnostiqueur certifié et enregistré dans la base ADEME. Pour l'usage qui est le vôtre, conseiller un client en rendez-vous ou évaluer une opération, le niveau de précision de DPEWizard est largement suffisant."
    },
    {
      question: "Les estimations de coût sont-elles sérieuses ?",
      answer: "Oui, parce que DPEWizard ne travaille pas avec des moyennes génériques. Le questionnaire contextuel permet de définir le type de travaux, les matériaux adaptés au bâti et les contraintes de faisabilité. Les coûts sont calculés sur cette base, pas sur une ligne « isolation des murs » sans autre précision. Mais sur une ligne « isolation par l'intérieur en laine de verre 120 mm pour un R=3.4, sans difficulté de pose » (par exemple). Ce niveau de détail est supérieur à la majorité des outils du marché."
    },
    {
      question: "Les rapports PDF peuvent-ils être à mon nom ?",
      answer: "Oui, avec l'offre Unlimited Power Pro. Vos rapports sortent avec votre logo et le nom de votre entreprise. Vous les remettez directement à vos clients."
    },
    {
      question: "L'outil est-il mis à jour avec les évolutions réglementaires ?",
      answer: "Oui. Les coefficients de malus électricité et les recalibrages de lettres pour les petites surfaces sont intégrés et mis à jour. Le coût des travaux est aussi remis à jour plusieurs fois par an."
    },
    {
      question: "Peut-on faire autant d'analyses que nécessaire ?",
      answer: "Avec les offres Unlimited Power et Unlimited Power Pro, les analyses sont illimitées, de même que les scénarios de travaux pour chaque bien."
    },
    {
      question: "Faut-il être expert en rénovation pour utiliser l'outil ?",
      answer: "Non. Le questionnaire est conçu pour être accessible à n'importe quel professionnel, sans connaissance technique en travaux. Les questions sont simples, les réponses sont guidées. Toute la complexité est absorbée par l'outil."
    },
    {
      question: "Combien de temps prend une analyse complète ?",
      answer: "Entre 2 et 5 minutes selon le bien et les réponses au questionnaire."
    },
    {
      question: "Peut-on comparer plusieurs scénarios de travaux ?",
      answer: "Oui. Il est possible de créer autant de scénarios que nécessaire sur un même bien et de les comparer côte à côte."
    },
    {
      question: "L'outil fonctionne-t-il sur tous les DPE ?",
      answer: "DPEWizard fonctionne sur tous les DPE réalisés depuis 2021 selon la méthode 3CL et enregistrés dans la base ADEME. Les DPE antérieurs à la réforme de 2021 ne sont pas compatibles car ils reposent sur une méthode de calcul différente."
    },
    {
      question: "DPEWizard fonctionne-t-il avec les audits énergétiques ?",
      answer: "Non. Les audits énergétiques utilisent un format et une base de données distincts, non accessibles publiquement. Aucun outil du marché ne propose cette approche pour les audits, faute de données disponibles. Si vous saisissez un numéro d'audit par erreur, l'outil vous en informe directement."
    },
    {
      question: "Les aides financières sont-elles intégrées ?",
      answer: "Non, volontairement. Les aides dépendent de plus de 400 critères individuels et varient selon le profil du propriétaire, la nature des travaux, la localisation et d'autres paramètres qu'aucun outil ne peut garantir. DPEWizard fournit des liens vers les simulateurs officiels. Promettre des montants d'aides sans cette vérification serait trompeur."
    },
    {
      question: "Peut-on utiliser l'outil directement en rendez-vous client ?",
      answer: "Oui, c'est précisément pour ça qu'il a été conçu. L'analyse se fait en temps réel, le rapport est généré immédiatement et peut être remis ou envoyé au client avant la fin du rendez-vous."
    },
    {
      question: "Plusieurs utilisateurs peuvent-ils partager un compte ?",
      answer: "L'abonnement est nominatif. Pour un usage en réseau ou en agence avec plusieurs collaborateurs, contactez-nous pour une offre adaptée."
    }
  ];

  return (
    <section className="py-20 bg-gray-50" id="faq-pro">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16">
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 leading-tight uppercase">
            Questions fréquentes
          </h2>
          <div className="w-12 h-0.5 bg-blue-500 mb-6"></div>
          <p className="text-lg text-gray-600 max-w-4xl">
            Fiabilité, estimations de coût, fonctionnalités et usage professionnel
          </p>
        </div>

        <div className="space-y-4 max-w-4xl mx-auto">
          {faqItems.map((item, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden transition-all duration-200 shadow-sm"
            >
              <button
                className="w-full flex items-center justify-between p-6 text-left focus:outline-none hover:bg-gray-50 transition-colors"
                onClick={() => toggleQuestion(index)}
                aria-expanded={openIndex === index}
                aria-controls={`pro-faq-answer-${index}`}
              >
                <h3 className="text-lg font-medium text-gray-900">{item.question}</h3>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0 ml-4" />
                )}
              </button>

              <div
                id={`pro-faq-answer-${index}`}
                className={`px-6 overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 pb-6' : 'max-h-0'
                }`}
              >
                <p className="text-gray-600">{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProFAQ;
